import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { SHADOW_CARD, SHADOW_POPOVER } from './styles';
import { Body1, Body5, Body7, Container } from '@ui';
import { FlatButton, TransparentFlatButton, CollapseButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { colorThemeActions } from '@constants/actionTypes';
import { APP_COLOR_THEME_NAMES } from '@theme/appThemes';
import { exportThemeAsJSON, downloadJSON, persistColorThemeName, _getThemesFromStorage } from '@utils/themes';
import { showSuccessBanner } from '@utils/application';
import { breakpoints } from '@constants/breakpoints';
import { ConfirmModal } from './ConfirmModal';
import { ThemeImporter } from './ThemeImporter';

const SectionWrapper = styled.div`
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border-radius: 8px;
  box-shadow: ${SHADOW_CARD};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${({ theme, $open }) => ($open ? theme.themeColors.lowContrastBorder : 'transparent')};
  transition: border-color 120ms ease;

  &:hover {
    opacity: 0.85;
  }
`;

const SectionBody = styled.div`
  padding: 20px;
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: ${() => breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ThemeCardWrapper = styled.div`
  background: ${({ theme }) => theme.themeColors.appBackground};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  padding: 14px;
  transition: box-shadow 160ms ease;

  &:hover {
    box-shadow: ${SHADOW_POPOVER};
  }
`;

const MiniPaletteRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`;

const MiniSwatch = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${({ $color }) => $color || '#ccc'};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  flex-shrink: 0;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
`;

const SmallButtonWrap = styled.div`
  button {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const DeleteWrap = styled.div`
  margin-left: auto;

  button {
    padding: 6px 12px;
    font-size: 12px;
  }

  button * {
    color: ${({ theme }) => theme.themeColors.error} !important;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
`;

const ImportDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin: 20px 0;
`;

// CollapseButton has padding-bottom: 8px baked in — this wrapper cancels it so
// the chevron centers correctly in any align-items: center flex row.
const CollapseAlignFix = styled.div`
  margin-bottom: -8px;
`;

const MINI_PALETTE_KEYS = ['appBackground', 'componentNoOpacity', 'primaryCtaButton', 'text', 'positive'];

const MiniPalette = ({ themeColors }) => (
  <MiniPaletteRow>
    {MINI_PALETTE_KEYS.map((k) => (
      <MiniSwatch key={k} $color={themeColors[k]} title={k} />
    ))}
  </MiniPaletteRow>
);

const ThemeCard = ({ name, themeColors, onDeleted }) => {
  const dispatch = useDispatch();
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const handleLoad = () => {
    dispatch({
      type: colorThemeActions.setCustomColorThemeColorDefinition,
      payload: { ...themeColors },
    });
    // Storage says custom from here on, so the running app has to agree -- otherwise the theme
    // only appears on the next refresh.
    dispatch({ type: colorThemeActions.set, payload: APP_COLOR_THEME_NAMES.custom });
    persistColorThemeName(APP_COLOR_THEME_NAMES.custom, { customThemeName: name });
    showSuccessBanner(`Loaded "${name}"`, {}, dispatch);
  };

  const handleExport = () => {
    const { json, filename } = exportThemeAsJSON(name, themeColors);
    downloadJSON(json, filename);
    showSuccessBanner(`Exported "${name}"`, {}, dispatch);
  };

  const handleDelete = () => {
    const themes = _getThemesFromStorage();
    delete themes[name];
    window.localStorage.setItem('colorThemes', JSON.stringify(themes));
    onDeleted();
    showSuccessBanner(`Deleted "${name}"`, {}, dispatch);
  };

  return (
    <>
      <ThemeCardWrapper>
        <MiniPalette themeColors={themeColors} />
        <Body5 bold truncate>
          {name}
        </Body5>
        <CardActions>
          <SmallButtonWrap>
            <FlatButton onClick={handleLoad}>Load</FlatButton>
          </SmallButtonWrap>
          <SmallButtonWrap>
            <SkeletonButton onClick={handleExport}>Export</SkeletonButton>
          </SmallButtonWrap>
          <DeleteWrap>
            <TransparentFlatButton onClick={() => setConfirmDelete(true)} aria-label={`Delete theme ${name}`}>
              Delete
            </TransparentFlatButton>
          </DeleteWrap>
        </CardActions>
      </ThemeCardWrapper>

      {confirmDelete && (
        <ConfirmModal
          title="Delete theme?"
          body={`This will permanently remove "${name}" from your saved themes.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          dangerous
        />
      )}
    </>
  );
};

export const SavedThemesSection = ({ themesInStorage, onRefresh }) => {
  const themeEntries = Object.entries(themesInStorage);
  const hasThemes = themeEntries.length > 0;
  const [open, setOpen] = React.useState(hasThemes);

  return (
    <SectionWrapper>
      <SectionHeader $open={open} onClick={() => setOpen((v) => !v)}>
        <Container>
          <Body1 bold>Saved themes</Body1>
          {hasThemes && (
            <Container top={2}>
              <Body7 isLowContrast>
                {themeEntries.length} theme{themeEntries.length !== 1 ? 's' : ''} saved locally
              </Body7>
            </Container>
          )}
        </Container>
        <CollapseAlignFix>
          <CollapseButton isCollapsed={!open} setCollapsed={(v) => setOpen(!v)} />
        </CollapseAlignFix>
      </SectionHeader>

      {open && (
        <SectionBody>
          <ThemeImporter onImported={onRefresh} />

          {hasThemes && <ImportDivider />}

          {hasThemes ? (
            <ThemeGrid>
              {themeEntries.map(([name, themeColors]) => (
                <ThemeCard key={name} name={name} themeColors={themeColors} onDeleted={onRefresh} />
              ))}
            </ThemeGrid>
          ) : (
            <EmptyState>
              <Body5 isLowContrast>No saved themes yet — import one above or save a draft.</Body5>
            </EmptyState>
          )}
        </SectionBody>
      )}
    </SectionWrapper>
  );
};
